import { Clipboard, Search, Users } from "lucide-react";
import { useState } from "react";

import { editResearchProfile, EditResearchType } from "@/api/profile";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MoonLoader } from "react-spinners";

import { useProfile } from "@/hooks/server-state/use-profile";

import SaveButton from "../_components/save-button";
import TextareaUi from "../_components/textarea";
import TextfieldRepeater from "../_components/textfieald-reapeter";

const Loading = () => {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-white">
      <MoonLoader color="#0099A5" size={50} />
    </div>
  );
};

const EditProfileResearch = () => {
  const { data: profileData, isLoading: isProfileLoading } = useProfile();

  const isLoading = isProfileLoading;

  const [editData, setEditData] = useState<EditResearchType>({
    id: profileData?.id,
    research: profileData?.research,
    researchFA: profileData?.researchFA,
    researchAreas: profileData?.researchAreas,
    areaOfStudy: profileData?.areaOfStudy,
    areaOfStudyFa: profileData?.areaOfStudyFA,
    membershipEn: profileData?.membershipEn,
    membershipFa: profileData?.membershipFa,
    memberships: profileData?.memberships,
    professionalActivities: profileData?.professionalActivities,
    professionalActivityEn: profileData?.professionalActivityEn,
    professionalActivityFa: profileData?.professionalActivityFa
  });

  const queryClient = useQueryClient();

  const mutateData = useMutation({
    mutationFn: (data: EditResearchType) => editResearchProfile(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["profile"] })
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    mutateData.mutate(editData);
  };

  if (isLoading) return <Loading />;
  return (
    <form onSubmit={handleSubmit}>
      <div className="px-10 py-5 pb-10">
        <div className="grid grid-cols-1 gap-x-10 gap-y-20 sm:grid-cols-2">
          <div className="space-y-3">
            <TextareaUi
              placeholder="لطفا علاقه مندی های پژوهشی خود را در قالب متن در اینجا وارد کنید ..."
              defaultValue={profileData?.researchFA}
              onChange={(e) => {
                setEditData((prev) => ({
                  ...prev,
                  researchFA: e.target.value
                }));
              }}
              label={
                <div className="flex items-center gap-x-1">
                  <Search size={16} />
                  علاقه‌مندی پژوهشی
                </div>
              }
              className="h-[180px] w-full"
            />
            <TextfieldRepeater
              defaultValues={profileData?.researchAreas.map(
                (research) => research.titleFa
              )}
              onValuesChange={(values) => {
                setEditData((prev) => ({
                  ...prev,
                  researchAreas: (prev.researchAreas || [])
                    .map((area, index) => ({
                      ...area,
                      titleFa: values[index] || ""
                    }))
                    .concat(
                      values
                        .slice((prev.researchAreas || []).length)
                        .map((value) => ({
                          titleFa: value
                        }))
                    )
                }));
              }}
            />
          </div>
          <div className="space-y-3" dir="ltr">
            <TextareaUi
              placeholder="Please enter your research interests here in text format..."
              defaultValue={profileData?.research}
              onChange={(e) => {
                setEditData((prev) => ({
                  ...prev,
                  research: e.target.value
                }));
              }}
              label={
                <div className="flex items-center gap-x-1">
                  <Search size={16} />
                  Research Interests
                </div>
              }
              className="h-[180px] w-full"
            />
            <TextfieldRepeater
              defaultValues={profileData?.researchAreas.map(
                (research) => research.title
              )}
              onValuesChange={(values) => {
                setEditData((prev) => ({
                  ...prev,
                  researchAreas: (prev.researchAreas || [])
                    .map((area, index) => ({
                      ...area,
                      title: values[index] || ""
                    }))
                    .concat(
                      values
                        .slice((prev.researchAreas || []).length)
                        .map((value) => ({
                          title: value
                        }))
                    )
                }));
              }}
            />
          </div>
          <div className="space-y-3">
            <TextareaUi
            placeholder="لطفا حوضه تخصصی خود را در قالب متن در اینجا وارد کنید"
              defaultValue={profileData?.professionalActivityFa}
              onChange={(e) => {
                setEditData((prev) => ({
                  ...prev,
                  professionalActivityFa: e.target.value
                }));
              }}
              label={
                <div className="flex items-center gap-x-1">
                  <Clipboard size={16} />
                  حوزه‌های تخصصی
                </div>
              }
              className="h-[180px] w-full"
            />
            <TextfieldRepeater
              defaultValues={profileData?.professionalActivities.map(
                (activity) => activity.titleFa
              )}
              onValuesChange={(values) => {
                setEditData((prev) => ({
                  ...prev,
                  professionalActivities: (prev.professionalActivities || [])
                    .map((area, index) => ({
                      ...area,
                      titleFa: values[index] || ""
                    }))
                    .concat(
                      values
                        .slice((prev.professionalActivities || []).length)
                        .map((value) => ({
                          titleFa: value
                        }))
                    )
                }));
              }}
            />
          </div>
          <div className="space-y-3" dir="ltr">
            <TextareaUi
            placeholder="Please enter your area of expertise here in text format..."
              defaultValue={profileData?.professionalActivityEn}
              onChange={(e) => {
                setEditData((prev) => ({
                  ...prev,
                  professionalActivityEn: e.target.value
                }));
              }}
              label={
                <div className="flex items-center gap-x-1">
                  <Clipboard size={16} />
                  Areas of Expertise
                </div>
              }
              className="h-[180px] w-full"
            />
            <TextfieldRepeater
              defaultValues={profileData?.professionalActivities.map(
                (activity) => activity.title
              )}
              onValuesChange={(values) => {
                setEditData((prev) => ({
                  ...prev,
                  professionalActivities: (prev.professionalActivities || [])
                    .map((area, index) => ({
                      ...area,
                      title: values[index] || ""
                    }))
                    .concat(
                      values
                        .slice((prev.professionalActivities || []).length)
                        .map((value) => ({
                          title: value
                        }))
                    )
                }));
              }}
            />
          </div>
          <div className="space-y-3">
            <TextareaUi
            placeholder="لطفا سابقه خود را در قالب متن در اینجا وارد کنید"
              defaultValue={profileData?.membershipFa}
              onChange={(e) => {
                setEditData((prev) => ({
                  ...prev,
                  membershipFa: e.target.value
                }));
              }}
              label={
                <div className="flex items-center gap-x-1">
                  <Users size={16} />
                  عضویت در انجمن‌ها و گروه‌های تحقیقاتی
                </div>
              }
              className="h-[180px] w-full"
            />
            <TextfieldRepeater
              defaultValues={profileData?.memberships.map(
                (membership) => membership.titleFa
              )}
              onValuesChange={(values) => {
                setEditData((prev) => ({
                  ...prev,
                  memberships: (prev.memberships || [])
                    .map((area, index) => ({
                      ...area,
                      titleFa: values[index] || ""
                    }))
                    .concat(
                      values
                        .slice((prev.memberships || []).length)
                        .map((value) => ({
                          titleFa: value
                        }))
                    )
                }));
              }}
            />
          </div>
          <div className="space-y-3" dir="ltr">
            <TextareaUi
            placeholder="Please enter your background here in text format..."
              defaultValue={profileData?.membershipEn}
              onChange={(e) => {
                setEditData((prev) => ({
                  ...prev,
                  membershipEn: e.target.value
                }));
              }}
              label={
                <div className="flex items-center gap-x-1">
                  <Users size={16} />
                  Membership of Committiees and Professional Bodies
                </div>
              }
              className="h-[180px] w-full"
            />
            <TextfieldRepeater
              defaultValues={profileData?.memberships.map(
                (membership) => membership.title
              )}
              onValuesChange={(values) => {
                setEditData((prev) => ({
                  ...prev,
                  memberships: (prev.memberships || [])
                    .map((area, index) => ({
                      ...area,
                      title: values[index] || ""
                    }))
                    .concat(
                      values
                        .slice((prev.memberships || []).length)
                        .map((value) => ({
                          title: value
                        }))
                    )
                }));
              }}
            />
          </div>
        </div>
      </div>
      <SaveButton />
    </form>
  );
};
export default EditProfileResearch;
